import React from 'react';
import { Layout } from 'fronton-react';
import { useStoreon } from 'storeon/react';
import { useLocation } from '@okapi/router-react';
import { RemoteCarouselWithBasket, RootLayout } from 'common/components';
import {
  Pdp,
  ProductComposition,
  ProductDescription,
  ProductInfo,
  ProductMedia,
  ProductStickyPanel,
} from 'common/components/pdp';
import { useEffectOnce } from 'common/hooks';
import { COLLECTIONS_STORE_NAME, selectCollectionInfo, VariantAxisProduct } from 'modules/collections';
import { useRegionIdSelector } from 'modules/model-json';
import { RelatedService } from 'modules/related-services';
import { State } from 'store/types';
import { analyticsUtils } from 'utils/analytics-utils';
import { removeSpecialCharacters } from 'utils/format';
import {
  addToCartGAEvent,
  previewClickGAEvent,
  serviceClickGAEvent,
  serviceShowGAEvent,
  showMoreClickGAEvent,
} from 'utils';
import { BathroomPdpCategoryCardSection } from './components';
import {
  BATHROOM_PDP_ELEMENT_BLOCK,
  BATHROOM_PDP_LANDING_NAME,
  BATHROOM_PDP_NOT_FORGET_LIST,
} from './bathroom-pdp-constants';

export const BathroomPdp = () => {
  const [path] = useLocation();
  const { collections } = useStoreon<State>(COLLECTIONS_STORE_NAME);
  const regionId = useRegionIdSelector();
  const collectionInfo = selectCollectionInfo(collections);
  const activePoint = collectionInfo?.variants?.activePoint || ({} as VariantAxisProduct);
  const basketHref = `/zapis-onlayn/?department_no=6&region_id=${regionId}`;

  const productName = removeSpecialCharacters(activePoint.displayedName);

  useEffectOnce(() => {
    if (activePoint) {
      analyticsUtils.sendEvent('GA:detail', {
        elementType: 'page',
        elementBlock: BATHROOM_PDP_ELEMENT_BLOCK, // блок, в котором расположен элемент
        productName, // название комплекта
        productId: activePoint.productId, // идентификатор комплекта
        productPrice: activePoint.price, // показанная стоимость комплекта
        productPrevPrice: null, // стоимость единицы товара до снижения цены или null
        productBelong: 'LeroyMerlin', // принадлежность товара
        komplektyId: activePoint.productId, // идентификатор комплекта
        division: 'Сантехника', // отдел, к которому относится товар
        subdivision: 'Набор мебели для ванной комнаты', // подотдел, к которому относится товар
        abTestPrice: null,
      });
    }
  });

  const handleAddToCartClickSticky = () => {
    addToCartGAEvent(activePoint, BATHROOM_PDP_ELEMENT_BLOCK, 'sticky-menu');
  };

  const handleAddToCartClick = () => {
    addToCartGAEvent(activePoint, BATHROOM_PDP_ELEMENT_BLOCK);
  };

  const handleOnShowMoreClick = (buttonName: string, elementName: string) => {
    showMoreClickGAEvent(buttonName, elementName, activePoint.productId, BATHROOM_PDP_ELEMENT_BLOCK);
  };

  const handleServiceShowEvent = (service: RelatedService, position: number) => {
    serviceShowGAEvent(activePoint, service, position, BATHROOM_PDP_ELEMENT_BLOCK);
  };

  const handleServiceClick = (service: RelatedService, position: number) => {
    serviceClickGAEvent(activePoint, service, position, BATHROOM_PDP_ELEMENT_BLOCK);
  };

  const handlePreviewClick = (index: number) => {
    previewClickGAEvent(activePoint, activePoint.mediaPhoto[index], BATHROOM_PDP_ELEMENT_BLOCK);
  };

  return (
    <RootLayout currentPage={path}>
      <Pdp
        productMedia={
          <ProductMedia
            collectionInfo={activePoint}
            onPreviewClick={handlePreviewClick}
            landingName={BATHROOM_PDP_LANDING_NAME}
          />
        }
        productInfo={
          <ProductInfo
            areAllSizesUnavailable={!activePoint.available}
            basketHref={basketHref}
            buttonLabel="Запись на проектирование"
            disclaimer="Без учета сантехники и&nbsp;ванны"
            infoNote={[
              'На\u00A0фотографии представлен демонстрационный образец.',
              'Для проверки наличия товаров добавьте гарнитур в\u00A0корзину.',
            ]}
            onAddToCartClick={handleAddToCartClick}
            onServiceClick={handleServiceClick}
            onServiceShowEvent={handleServiceShowEvent}
          />
        }>
        <ProductStickyPanel
          basketHref={basketHref}
          buttonLabel="Записаться"
          collectionInfo={activePoint}
          isCollectionAvailable={activePoint.available}
          landingName={BATHROOM_PDP_LANDING_NAME}
          onAddToCartClick={handleAddToCartClickSticky}
        />
        <Layout>
          {activePoint.available && (
            <ProductComposition
              collectionInfo={activePoint}
              productDivision="Сантехника"
              elementBlock={BATHROOM_PDP_ELEMENT_BLOCK}
              onShowMoreClick={handleOnShowMoreClick}
            />
          )}
          <ProductDescription
            description={activePoint.description || collectionInfo.description}
            onShowMoreClick={handleOnShowMoreClick}
          />
        </Layout>
        <RemoteCarouselWithBasket
          elementBlock={`${productName} / Не забудьте купить`}
          title="Не забудьте купить"
          productIdList={BATHROOM_PDP_NOT_FORGET_LIST}
          remoteId="doNotForgetToBuy"
        />
        <Layout>
          <BathroomPdpCategoryCardSection productId={activePoint.productId} />
        </Layout>
      </Pdp>
    </RootLayout>
  );
};
